import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';
import { NavigationClickEvent } from '@theme/events';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onNavLinkClick);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onNavLinkClick);
  }

  /**
   * Dispatch a `navigation:use` (GTM `navigation_use`) event when a drawer menu link is clicked.
   * @param {MouseEvent} event
   */
  #onNavLinkClick = (event) => {
    // TODO: code review, refactor
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link || !this.contains(link)) return;

    // Top-level menu the link belongs to (direct child of the root submenu list)
    const topItem = link.closest('.menu-drawer__menu.has-submenu > .menu-drawer__list-item');
    if (!topItem) return;

    const maincategory = navText(topItem.querySelector('.menu-drawer__menu-item--mainlist .menu-drawer__menu-item-text'));
    if (!maincategory) return;

    // Top-level link itself (simple or flat main link) → main category only
    if (link.matches('.menu-drawer__menu-item--mainlist')) {
      this.#dispatchNavigationClick(maincategory);
      return;
    }

    // "Alle {category}" view-all link → main category only
    if (link.matches('.menu-drawer__sub-link-all')) {
      this.#dispatchNavigationClick(maincategory);
      return;
    }

    // Level 3 grandchild link → main + parent + link
    const grandchildList = link.closest('.menu-drawer__menu--grandchildlist');
    if (grandchildList && link.matches('.menu-drawer__menu-item--child')) {
      const parentLink = grandchildList.previousElementSibling;
      const subcategory = parentLink?.matches('.menu-drawer__menu-item--parent')
        ? navText(parentLink.querySelector('.menu-drawer__menu-item-text'))
        : '';
      this.#dispatchNavigationClick(maincategory, subcategory, navText(link.querySelector('.menu-drawer__menu-item-text')));
      return;
    }

    // Level 2 link (parent in 3-level menus, or child in 2-level menus) → main + link
    if (link.matches('.menu-drawer__menu-item--parent, .menu-drawer__menu-item--child')) {
      this.#dispatchNavigationClick(maincategory, navText(link.querySelector('.menu-drawer__menu-item-text')));
    }
  };

  /**
   * @param {string} maincategory
   * @param {string} [subcategory]
   * @param {string} [subsubcategory]
   */
  #dispatchNavigationClick(maincategory, subcategory, subsubcategory) {
    if (!maincategory) return;
    document.dispatchEvent(new NavigationClickEvent(maincategory, subcategory, subsubcategory));
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Collapse whitespace in an element's text content for navigation labels.
 * @param {Element | null | undefined} el
 * @returns {string}
 */
function navText(el) {
  return (el?.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
